import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { purchasesApi } from "../../../api/inventory/purchases";
import { fetchCompany } from "../../../api/company";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { PurchaseLinesDialog } from "../../../components/dashboard/inventory/PurchaseLinesDialog";
import toast from "react-hot-toast";
import { createPurchaseAutosave, createPurchaseLineQueue, createPurchaseSaveChain } from "./purchase-autosave";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { PurchaseSendDialog } from "../../../components/dashboard/inventory/PurchaseSendDialog";
import { PurchaseActionEditBar } from "../../../components/dashboard/inventory/PurchaseActionEditBar";
import { ContactPicker, ProjectPicker } from "../../../components/utils/pickers";
import ModernProductSelectorDialog from "../../../components/utils/Selector/ModernProductSelectorDialog";
import {
  COMPANY_ADDRESS_FIELDS,
  companyFromCompanyResponse,
  defaultCompanyPurchaseAddress,
  DeliveryAddressPicker,
  formatDeliveryAddress,
} from "../../../components/utils/pickers/DeliveryAddressPicker";
import { formatDecimal } from "./inventory-format";
import { isValidInventoryQuantity, normalizeInventoryQuantity } from "./inventory-quantity";

const fieldClass = "min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100";
const lineFieldClass = "min-h-[40px] w-full min-w-[5.5rem] rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-blue-100";
const secondaryButton = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#1463C7] px-4 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400";

const lineKey = (id) => String(id);

function dateValue(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toISOString().split("T")[0];
}

function isValidInventoryPrice(value) {
  const price = String(value ?? "").trim().replace(",", ".");
  return /^\d+(?:\.\d{1,6})?$/.test(price);
}

function headerPayload(purchase) {
  return {
    project_id: purchase.project_id || null,
    contact_id: purchase.contact_id ?? purchase.contact?.id ?? null,
    delivery_address_id: purchase.delivery_address_id || null,
    delivery_address: purchase.delivery_address || "",
    cost_code: purchase.cost_code || "",
    purchase_date: purchase.purchase_date,
    delivery_date: purchase.delivery_date || null,
    send_date: purchase.send_date || null,
    reference: purchase.reference || "",
    comment: purchase.comment || "",
  };
}

function linePayload(item) {
  const payload = {
    product_id: item.product_id || null,
    quantity: normalizeInventoryQuantity(item.quantity),
    unit_id: item.unit_id || null,
    title: item.title || "",
    description: item.description || "",
  };
  if (item.purchase_price !== null && item.purchase_price !== undefined && item.purchase_price !== "") {
    payload.purchase_price = String(item.purchase_price).trim().replace(",", ".");
  }
  return payload;
}

function sumMoney(items, field) {
  if (!items?.length || items.some((item) => item[field] == null || item[field] === "")) return null;
  return items.reduce((sum, item) => sum + Number(item[field]), 0);
}

function ProductLineLink({ item }) {
  const { t } = useTranslation();
  const productId = item.product_id || item.product?.id;
  if (productId == null || productId === "") return null;
  return (
    <a
      href={`/dashboard/products/${productId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-[36px] items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-[#1463C7] hover:bg-slate-50"
    >
      {t("inventoryPurchase.product")}
    </a>
  );
}

function applyProductToLine(item, product) {
  return {
    ...item,
    product_id: product.id,
    product: {
      id: product.id,
      title: product.title,
      displayTitle: product.displayTitle || product.title,
      item_number: product.item_number,
    },
    supplier_product_id: null,
    supplier_product: null,
    title: product.title || product.displayTitle || "",
    unit_id: product.unit?.id || product.unit_id || item.unit_id || null,
    unit: product.unit || item.unit,
  };
}

export const Purchase = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canViewCost = roles.some((role) => role.name === "admin" || role.name === "inventory_finance");
  const [purchase, setPurchase] = useState(null);
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendOpening, setSendOpening] = useState(false);
  const [sendActing, setSendActing] = useState(false);
  const [productPickerItemId, setProductPickerItemId] = useState(null);
  const [localPurchase, setLocalPurchase] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lineSaving, setLineSaving] = useState(false);
  const [linesDirty, setLinesDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autosaveRef = useRef(null);
  const lineQueueRef = useRef(null);
  const chainRef = useRef(null);
  const versionRef = useRef(null);
  const itemsRef = useRef([]);
  const removedLineIds = useRef(new Set());
  const lineSaves = useRef(0);
  const defaultedForPurchase = useRef(null);
  const requestSeq = useRef(0);
  if (!chainRef.current) chainRef.current = createPurchaseSaveChain();

  const rememberVersion = (nextVersion) => {
    if (nextVersion == null) return;
    if (versionRef.current == null || Number(nextVersion) >= Number(versionRef.current)) {
      versionRef.current = nextVersion;
      autosaveRef.current?.noteVersion(nextVersion);
    }
  };

  const mergeIncomingPurchase = (incoming) => {
    const drafts = lineQueueRef.current?.drafts() || {};
    const items = (incoming.items || [])
      .filter((item) => !removedLineIds.current.has(lineKey(item.id)))
      .map((item) => drafts[lineKey(item.id)] ? { ...item, ...drafts[lineKey(item.id)] } : item);
    return { ...incoming, items };
  };

  useEffect(() => {
    const enqueue = chainRef.current;
    const markLineSaving = (delta) => {
      lineSaves.current += delta;
      setLineSaving(lineSaves.current > 0);
    };
    const queue = createPurchaseAutosave({
      save: (payload) => enqueue(async () => {
        const result = await purchasesApi.updatePurchase(id, { ...payload, version: versionRef.current });
        rememberVersion(result?.data?.data?.version);
        return result;
      }),
      onState: ({ saving, dirty, error: saveStatus, savedAt }) => {
        setIsSaving(saving);
        setHasUnsavedChanges(dirty);
        setSaveError(saveStatus === "conflict" ? t("inventoryPurchase.conflict") : saveStatus ? t("inventoryPurchase.unknownSave") : null);
        if (savedAt) setLastSaved(savedAt);
      },
      onSaved: (saved) => setPurchase((previous) => previous ? { ...previous, ...saved, items: previous.items } : saved),
    });
    const lines = createPurchaseLineQueue({
      save: (itemId, draft) => enqueue(async () => {
        if (removedLineIds.current.has(lineKey(itemId))) return { skipped: true };
        if (!isValidInventoryQuantity(draft.quantity)) return { skipped: true };
        if (draft.purchase_price !== null && draft.purchase_price !== undefined && draft.purchase_price !== "" && !isValidInventoryPrice(draft.purchase_price)) {
          return { skipped: true };
        }
        markLineSaving(1);
        try {
          const response = await purchasesApi.updateItemInPurchase(id, itemId, {
            version: versionRef.current,
            ...linePayload(draft),
          });
          rememberVersion(response?.data?.version);
          return response;
        } finally {
          markLineSaving(-1);
        }
      }),
      onLine: (itemId, event) => {
        setLinesDirty(lines.dirty());
        if (removedLineIds.current.has(lineKey(itemId))) return;
        if (event.status === "error") {
          const errorMessage = event.error?.response?.data?.errors?.join(", ") || event.error?.response?.data?.error || t("inventoryPurchase.unknownSave");
          toast.error(errorMessage);
          return;
        }
        if (event.status !== "saved" || event.result?.data?.success !== true) return;
        const updatedItem = event.result.data.data;
        const nextVersion = event.result.data.version;
        setPurchase((previous) => {
          if (!previous) return previous;
          if (lines.dirty() && lines.drafts()[lineKey(itemId)]) {
            return { ...previous, version: nextVersion ?? previous.version };
          }
          const items = previous.items.map((row) => lineKey(row.id) === lineKey(itemId) ? updatedItem : row);
          itemsRef.current = items;
          return {
            ...previous,
            version: nextVersion ?? previous.version,
            items,
            total_amount: sumMoney(items, "total_amount"),
            total_amount_incl_vat: sumMoney(items, "total_amount_incl_vat"),
          };
        });
        setLastSaved(new Date().toISOString());
      },
    });
    autosaveRef.current = queue;
    lineQueueRef.current = lines;
    return () => {
      queue.dispose();
      lines.dispose();
      if (autosaveRef.current === queue) autosaveRef.current = null;
      if (lineQueueRef.current === lines) lineQueueRef.current = null;
    };
  }, [id, companyId, t]);

  useEffect(() => {
    setPurchase(null); setLoadedCompanyId(null); setLocalPurchase(null); setError(null);
    removedLineIds.current = new Set();
    defaultedForPurchase.current = null;
    versionRef.current = null;
    fetchPurchase();
    return () => { requestSeq.current += 1; };
  }, [id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPurchase = async () => {
    const sequence = ++requestSeq.current;
    try {
      setLoading(true); setError(null);
      const response = await purchasesApi.getPurchase(id);
      if (sequence !== requestSeq.current) return;
      if (response?.data?.success) {
        const purchaseData = mergeIncomingPurchase(response.data.data);
        itemsRef.current = purchaseData.items || [];
        setPurchase(purchaseData);
        setLoadedCompanyId(companyId);
        rememberVersion(purchaseData.version);
        if (autosaveRef.current?.hydrate(purchaseData.version) !== false) {
          setLocalPurchase(purchaseData);
          setHasUnsavedChanges(false);
          setSaveError(null);
        }
        setLastSaved(new Date().toISOString());
      } else {
        setError(t("inventoryPurchase.loadError"));
      }
    } catch (err) {
      if (sequence === requestSeq.current) setError(t("inventoryPurchase.loadError"));
    } finally {
      if (sequence === requestSeq.current) setLoading(false);
    }
  };

  const addressBlank = Boolean(localPurchase) && !localPurchase.delivery_address_id && !String(localPurchase.delivery_address || "").trim();

  useEffect(() => {
    if (!canViewCost || !addressBlank || defaultedForPurchase.current === String(id)) return undefined;
    let cancelled = false;
    fetchCompany({ fields: COMPANY_ADDRESS_FIELDS })
      .then((payload) => {
        if (cancelled) return;
        const address = defaultCompanyPurchaseAddress(companyFromCompanyResponse(payload));
        if (!address?.id) return;
        defaultedForPurchase.current = String(id);
        setLocalPurchase((current) => {
          if (!current || current.delivery_address_id || String(current.delivery_address || "").trim()) return current;
          const next = {
            ...current,
            delivery_address_id: address.id,
            selected_delivery_address: address,
            delivery_address: formatDeliveryAddress(address),
          };
          autosaveRef.current?.edit(headerPayload(next));
          return next;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, canViewCost, addressBlank]);

  const handleHeaderPatch = (changes) => {
    const updated = { ...localPurchase, ...changes };
    setLocalPurchase(updated);
    autosaveRef.current?.edit(headerPayload(updated));
  };
  const handleFieldChange = (field, value) => handleHeaderPatch({ [field]: value });

  const patchLine = (itemId, changes, { immediate = false } = {}) => {
    const nextItems = itemsRef.current.map((item) => lineKey(item.id) === lineKey(itemId) ? { ...item, ...changes } : item);
    itemsRef.current = nextItems;
    setPurchase((previous) => previous ? { ...previous, items: nextItems } : previous);
    const next = nextItems.find((item) => lineKey(item.id) === lineKey(itemId));
    if (!next) return;
    lineQueueRef.current?.edit(itemId, next);
    setLinesDirty(true);
    if (immediate) lineQueueRef.current?.flush(itemId);
  };

  const changeLineProduct = (itemId, product) => {
    if (!product?.id) return;
    const current = itemsRef.current.find((item) => lineKey(item.id) === lineKey(itemId));
    if (!current) return;
    const next = applyProductToLine(current, product);
    patchLine(itemId, next, { immediate: true });
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm(t("inventoryPurchase.removeConfirm"))) return;
    removedLineIds.current.add(lineKey(itemId));
    lineQueueRef.current?.forget(itemId);
    setLinesDirty(Boolean(lineQueueRef.current?.dirty()));
    try {
      const response = await chainRef.current(() => purchasesApi.removeItemFromPurchase(id, itemId, versionRef.current));
      if (response?.data?.success) {
        rememberVersion(response.data.version);
        const items = itemsRef.current.filter((item) => lineKey(item.id) !== lineKey(itemId));
        itemsRef.current = items;
        setPurchase((previous) => previous ? {
          ...previous,
          version: response.data.version ?? previous.version,
          items,
          total_amount: sumMoney(items, "total_amount"),
          total_amount_incl_vat: sumMoney(items, "total_amount_incl_vat"),
        } : previous);
        setLastSaved(new Date().toISOString());
        toast.success(t("inventoryPurchase.lineRemoved"));
      } else {
        removedLineIds.current.delete(lineKey(itemId));
        const errorMessage = response?.data?.errors?.join(", ") || t("inventoryPurchase.removeFailed");
        toast.error(errorMessage);
      }
    } catch (removeError) {
      removedLineIds.current.delete(lineKey(itemId));
      const errorMessage = removeError.response?.data?.errors?.join(", ") || removeError.response?.data?.error || t("inventoryPurchase.removeFailed");
      toast.error(errorMessage);
    }
  };

  const flushUnsavedPurchase = async () => {
    const headerOk = (await autosaveRef.current?.flush()) !== false;
    const linesOk = (await lineQueueRef.current?.flushAll?.()) !== false;
    await chainRef.current(() => Promise.resolve());
    return headerOk && linesOk && !autosaveRef.current?.blocked && !lineQueueRef.current?.dirty?.();
  };

  const handleOpenSend = async () => {
    if (sendOpening) return;
    setSendOpening(true);
    try {
      const ready = await flushUnsavedPurchase();
      if (!ready) {
        toast.error(t("inventoryPurchase.sendNeedsSave"));
        return;
      }
      setSendDialogOpen(true);
    } finally {
      setSendOpening(false);
    }
  };

  const handleMarkAsSent = async () => {
    if (sendActing) return;
    const ready = await flushUnsavedPurchase();
    if (!ready) {
      toast.error(t("inventoryPurchase.sendNeedsSave"));
      return;
    }
    setSendActing(true);
    try {
      const response = await purchasesApi.markAsSent(id);
      if (!response?.data?.success) {
        throw new Error(response?.data?.errors?.join(", ") || t("inventoryPurchase.markSentFailed"));
      }
      toast.success(t("inventoryPurchase.markSentSuccess"));
      await fetchPurchase();
      setLastSaved(new Date().toISOString());
    } catch (error) {
      const message = error?.response?.data?.errors?.filter(Boolean).join(", ")
        || error?.response?.data?.problem?.message
        || error?.message
        || t("inventoryPurchase.markSentFailed");
      toast.error(message);
    } finally {
      setSendActing(false);
    }
  };

  const handleMarkAsConfirmed = async () => {
    if (sendActing) return;
    setSendActing(true);
    try {
      const response = await purchasesApi.markAsConfirmed(id);
      if (!response?.data?.success) {
        throw new Error(response?.data?.errors?.join(", ") || t("inventoryPurchase.markConfirmedFailed"));
      }
      toast.success(t("inventoryPurchase.markConfirmedSuccess"));
      await fetchPurchase();
      setLastSaved(new Date().toISOString());
    } catch (error) {
      const message = error?.response?.data?.errors?.filter(Boolean).join(", ")
        || error?.response?.data?.problem?.message
        || error?.message
        || t("inventoryPurchase.markConfirmedFailed");
      toast.error(message);
    } finally {
      setSendActing(false);
    }
  };

  const handleCreateGoodsReceipt = () => {
    navigate(`/dashboard/inventory/goods-receipts/new?purchase_id=${id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "sent":
        return "bg-blue-100 text-blue-800";
      case "confirmed":
        return "bg-yellow-100 text-yellow-800";
      case "received":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => t(`inventoryPurchase.status.${status}`, status);

  if (loading || (!error && loadedCompanyId !== companyId)) {
    return (
      <>
        <DashboardHeader title={t("inventoryPurchase.title")} subtitle={t("inventoryPurchase.loading")} />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error || !purchase) {
    return (
      <>
        <DashboardHeader title={t("inventoryPurchase.title")} subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error || t("inventoryPurchase.notFound")]} />
        </div>
      </>
    );
  }

  const canSend = canViewCost && purchase.status === "draft" && purchase.items && purchase.items.length > 0;
  const sendBusy = sendOpening || sendActing || isSaving || lineSaving;
  const canConfirm = canViewCost && purchase.status === "sent";
  const canCreateGoodsReceipt = (purchase.status === "confirmed" || purchase.status === "sent") && purchase.items && purchase.items.length > 0;
  const productPickerItem = purchase.items?.find((item) => lineKey(item.id) === lineKey(productPickerItemId));

  return (
    <>
      <DashboardHeader
        title={`${t("inventoryPurchase.title")} ${purchase.purchase_number || ""}`}
        subtitle={purchase.supplier?.displayTitle || purchase.supplier?.name || ""}
        actions={[
          {
            component: null,
            label: t("inventoryPurchase.back"),
            description: "",
            path: "/dashboard/inventory/purchases",
            action: null,
            icon: ArrowLeftIcon,
            textColor: "text-gray-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
          {
            component: (
              <HistoriesModalButton module="purchase" module_param={purchase?.id} />
            ),
            label: t("histories.title"),
            description: "",
            textColor: "text-teal-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]}
      />
      <div className="min-h-full bg-slate-50 pb-24"><div className="mx-auto max-w-[1440px] space-y-5 px-4 pt-6 sm:px-6 lg:px-8">
          <PurchaseActionEditBar
            hasUnsavedChanges={hasUnsavedChanges || linesDirty}
            isSaving={isSaving || lineSaving}
            lastSaved={lastSaved}
            error={saveError}
          />

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(purchase.status)}`}>
                  {getStatusLabel(purchase.status)}
                </span>
                {purchase.delivery_state && <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-700">{t(`inventoryPurchase.delivery.${purchase.delivery_state}`)}</span>}
                {purchase.tripletex_sync?.state && <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-900">{t("inventoryPurchase.tripletexSync")}: {t(`inventoryPurchase.sync.${purchase.tripletex_sync.state}`)}</span>}
                {purchase.delivery_state === "queued" && <button type="button" className="text-sm font-semibold text-[#1463C7] underline" onClick={() => fetchPurchase()}>{t("inventoryValuation.refresh")}</button>}
              </div>
              <div className="flex flex-wrap gap-2">
                {canViewCost && <button type="button" className={secondaryButton} onClick={() => autosaveRef.current?.flush()} disabled={!hasUnsavedChanges || isSaving}>{t("inventoryPurchase.saveDraft")}</button>}
                {canSend && (
                  <button type="button" className={primaryButton} onClick={handleOpenSend} disabled={sendBusy}>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    {t("inventoryPurchase.sendToSupplier")}
                  </button>
                )}
                {canSend && (
                  <button type="button" className={secondaryButton} onClick={handleMarkAsSent} disabled={sendBusy}>
                    {t("inventoryPurchase.markSentManually")}
                  </button>
                )}
                {canConfirm && (
                  <button type="button" className={secondaryButton} onClick={handleMarkAsConfirmed}>
                    <CheckCircleIcon className="h-4 w-4" />
                    {t("inventoryPurchase.confirmed")}
                  </button>
                )}
                {canCreateGoodsReceipt && (
                  <button type="button" className={`${primaryButton} bg-emerald-600 hover:bg-emerald-700`} onClick={handleCreateGoodsReceipt}>
                    <TruckIcon className="h-4 w-4" />
                    {t("inventoryPurchase.goodsReceipt")}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-label={t("inventoryPurchase.details")}>
              <h2 className="mb-5 text-lg font-bold text-slate-900">{t("inventoryPurchase.details")}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <ProjectPicker
                  label={t("inventoryPurchase.project")}
                  value={localPurchase?.project || null}
                  disabled={!canViewCost}
                  onChange={(event) => {
                    handleHeaderPatch({
                      project_id: event.target.value || null,
                      project: event.target.object || null,
                    });
                  }}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="purchase-date">{t("inventoryPurchase.purchaseDate")}</label>
                <input id="purchase-date" className={fieldClass} type="date" value={dateValue(localPurchase?.purchase_date)} onChange={(event) => handleFieldChange("purchase_date", event.target.value)} disabled={!canViewCost} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="delivery-date">{t("inventoryPurchase.deliveryDate")}</label>
                <input id="delivery-date" className={fieldClass} type="date" value={dateValue(localPurchase?.delivery_date)} onChange={(event) => handleFieldChange("delivery_date", event.target.value)} disabled={!canViewCost} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="send-date">{t("inventoryPurchase.sendDate")}</label>
                <input id="send-date" className={fieldClass} type="date" value={dateValue(localPurchase?.send_date)} onChange={(event) => handleFieldChange("send_date", event.target.value)} disabled={!canViewCost} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="purchase-reference">{t("inventoryPurchase.reference")}</label>
                <input id="purchase-reference" className={fieldClass} value={localPurchase?.reference || ""} onChange={(event) => handleFieldChange("reference", event.target.value)} disabled={!canViewCost} />
              </div>
              <div className="md:col-span-2">
                <DeliveryAddressPicker
                  label={t("inventoryPurchase.deliveryAddress")}
                  value={localPurchase?.selected_delivery_address || null}
                  addressSet="purchase"
                  projectId={localPurchase?.project_id || localPurchase?.project?.id}
                  customerId={localPurchase?.project?.customer_id || localPurchase?.project?.customer?.id || null}
                  disabled={!canViewCost || ["queued", "accepted"].includes(localPurchase?.delivery_state)}
                  allowCreate={false}
                  onChange={(event) => {
                    const row = event.target.data;
                    handleHeaderPatch({
                      delivery_address_id: event.target.value,
                      selected_delivery_address: row,
                      delivery_address: row ? formatDeliveryAddress(row) : "",
                    });
                  }}
                />
                {!localPurchase?.delivery_address_id && localPurchase?.delivery_address ? <p className="mt-1 text-sm text-slate-600">{localPurchase.delivery_address}</p> : null}
                <p className="mt-1 text-xs text-amber-800">{t("inventoryPurchase.localAddressHint")}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="cost-code">{t("inventoryPurchase.costCode")}</label>
                <input id="cost-code" className={fieldClass} value={localPurchase?.cost_code || ""} maxLength={255} onChange={(event) => handleFieldChange("cost_code", event.target.value)} disabled={!canViewCost} />
                <p className="mt-1 text-xs text-slate-500">{t("inventoryPurchase.localCostCodeHint")}</p>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="purchase-comment">{t("inventoryPurchase.comment")}</label>
                <textarea id="purchase-comment" className={`${fieldClass} min-h-[96px] py-2`} rows={3} value={localPurchase?.comment || ""} onChange={(event) => handleFieldChange("comment", event.target.value)} disabled={!canViewCost} />
              </div>
              </div>
            </section>
            <aside className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("inventoryPurchases.supplier")}</p>
                <p className="mt-3 text-lg font-bold text-slate-900">{purchase.supplier?.displayTitle || purchase.supplier?.name || "—"}</p>
                {purchase.supplier?.email && <p className="mt-1 break-all text-sm text-slate-600">{purchase.supplier.email}</p>}
                <div className="mt-4">
                  <ContactPicker
                    label={t("inventoryPurchase.contact")}
                    value={localPurchase?.contact || null}
                    customer={purchase.supplier}
                    disabled={!canViewCost}
                    allowCreate
                    onChange={(event) => {
                      handleHeaderPatch({
                        contact_id: event.target.value || null,
                        contact: event.target.data || null,
                      });
                    }}
                  />
                  <p className="mt-1 text-xs text-slate-500">{t("inventoryPurchase.contactHint")}</p>
                </div>
              </div>
              {canViewCost && <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-900">{t("inventoryPurchase.summary")}</h3>
                <div className="mt-4 flex justify-between text-sm text-slate-600"><span>{t("inventoryPurchase.netAmount")}</span><span className="font-semibold text-slate-900">{purchase.total_amount == null ? t("inventoryValuation.missingCost") : `${formatDecimal(purchase.total_amount)} ${purchase.currency_code || "NOK"}`}</span></div>
                <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-sm font-bold text-slate-900"><span>{t("inventoryPurchase.grossAmount")}</span><span>{purchase.total_amount_incl_vat == null ? "—" : `${formatDecimal(purchase.total_amount_incl_vat)} ${purchase.currency_code || "NOK"}`}</span></div>
              </div>}
            </aside>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{t("inventoryPurchase.orderLines")}</h3>
              {canViewCost && <button type="button" className={primaryButton} onClick={() => setAddItemDialogOpen(true)}>
                <PlusIcon className="h-4 w-4" />
                {t("inventoryPurchase.addProduct")}
              </button>}
            </div>

            {purchase.items && purchase.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-left text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th scope="col" className="px-4 py-3">{t("inventoryPurchase.itemNumber")}</th>
                      <th scope="col" className="px-4 py-3">{t("inventoryPurchase.product")}</th>
                      <th scope="col" className="px-4 py-3">{t("inventoryPurchase.quantity")}</th>
                      <th scope="col" className="px-4 py-3">{t("inventoryPurchase.unit")}</th>
                      {canViewCost && <th scope="col" className="px-4 py-3">{t("inventoryPurchase.price")}</th>}
                      {canViewCost && <th scope="col" className="px-4 py-3">{t("inventoryPurchase.lineTotal")}</th>}
                      <th scope="col" className="px-4 py-3 text-right">{t("inventoryPurchase.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {purchase.items.map((item) => (
                      <tr key={item.id} className="align-top">
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.supplier_product?.item_number || item.product?.item_number || "—"}</td>
                        <td className="px-4 py-3">
                          {canViewCost ? (
                            <div className="space-y-2">
                              <button type="button" className="block max-w-xs text-left" onClick={() => setProductPickerItemId(item.id)}>
                                <span className="block truncate font-medium text-slate-900">{item.product?.displayTitle || item.title || t("inventoryPurchase.changeProduct")}</span>
                                <span className="text-xs font-semibold text-[#1463C7]">{t("inventoryPurchase.changeProduct")}</span>
                              </button>
                              <input aria-label={t("inventoryPurchase.lineTitle")} className={lineFieldClass} value={item.title || ""} onChange={(event) => patchLine(item.id, { title: event.target.value })} />
                              <ProductLineLink item={item} />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <span>{item.product?.displayTitle || item.title || "—"}</span>
                              <ProductLineLink item={item} />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {canViewCost ? (
                            <input aria-label={t("inventoryPurchase.quantity")} className={`${lineFieldClass} max-w-[8rem]`} type="number" min="0.0001" step="0.0001" value={item.quantity ?? ""} onChange={(event) => patchLine(item.id, { quantity: event.target.value })} />
                          ) : (
                            item.quantity ?? "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{item.unit?.name || item.unit?.symbol || "—"}</td>
                        {canViewCost && <td className="px-4 py-3">
                          <input aria-label={t("inventoryPurchase.price")} className={`${lineFieldClass} max-w-[8rem]`} type="number" min="0" step="0.01" value={item.purchase_price ?? ""} onChange={(event) => patchLine(item.id, { purchase_price: event.target.value })} />
                        </td>}
                        {canViewCost && <td className="px-4 py-3 whitespace-nowrap font-semibold tabular-nums text-slate-900">{item.total_amount == null ? t("inventoryValuation.missingCost") : `${formatDecimal(item.total_amount)} ${purchase.currency_code || "NOK"}`}</td>}
                        <td className="px-4 py-3 text-right">
                          {canViewCost && <button type="button" onClick={() => handleRemoveItem(item.id)} className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-rose-700 hover:bg-rose-50" title={t("inventoryPurchase.remove")}>
                            <TrashIcon className="h-4 w-4" />
                          </button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="mb-4 text-gray-500">{t("inventoryPurchase.emptyLines")}</p>
                {canViewCost && <button type="button" className={primaryButton} onClick={() => setAddItemDialogOpen(true)}>
                  {t("inventoryPurchase.addFirstProduct")}
                </button>}
              </div>
            )}
          </div>
        </div>
      </div>

      <ModernProductSelectorDialog
        open={productPickerItemId != null}
        multiple={false}
        selectedProducts={productPickerItem?.product?.id ? [productPickerItem.product] : []}
        onChange={(products) => {
          const product = products?.[0];
          if (product?.id && productPickerItemId != null) changeLineProduct(productPickerItemId, product);
        }}
        onClose={() => setProductPickerItemId(null)}
      />

      <PurchaseLinesDialog
        open={addItemDialogOpen}
        purchaseId={id}
        purchaseVersion={versionRef.current ?? purchase.version}
        supplierId={purchase.supplier_id || purchase.supplier?.id}
        canViewCost={canViewCost}
        onClose={() => setAddItemDialogOpen(false)}
        onSaved={(_rows, version) => { if (version != null) { rememberVersion(version); setPurchase((previous) => previous ? ({ ...previous, version }) : previous); } fetchPurchase(); toast.success(t("inventoryPurchase.linesAdded")); }}
      />

      <PurchaseSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        purchase={localPurchase || purchase}
        onSuccess={() => {
          fetchPurchase();
          setSendDialogOpen(false);
        }}
      />
    </>
  );
};
